import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft, FileText, Image, Calendar, CheckCircle, User, Clock, Edit, } from 'lucide-react';
// import { mockAppointments, mockConsentForms } from '../data/mockData';
import Modal from '../components/Common/Modal';
import CustomSelect from '../components/Common/CustomSelect';
import api from '../services/api';
import { toast } from 'react-toastify';
import ClinicalSummary from './ClinicalSummary';
import ClinicalNotes from './ClinicalNotes';
import HistologyReports from './HistologyReports';
import Imaging from './Imaging';
import { getPatientById } from '../services/patientService';
import { getImagingReports} from '../services/imagingService';
import { getClinicalNotesByPatientId } from '../services/clinicalNotes';
import { getHistologyReportsByPatientId } from '../services/histologyReportServices';
import { Patient, ClinicalNote, HistologyReport, } from '../types';
import { ImagingReport } from '../services/imagingService';
import { createMDTReport } from '../services/mdtReportService';
import { useAuth } from "../contexts/AuthContext";
import MDTReportsTab from './MDTReportsTab';


const PatientProfile: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();

  // Patient state from API
  const [patient, setPatient] = useState<Patient | null>(null);
  const [loading, setLoading] = useState(true);

  const [notes, setNotes] = useState<ClinicalNote[]>([]);
  const [histologyReportsList,setHistologyReportsList] = useState<HistologyReport[]>([]);
  const [imaging,setImaginingReport] = useState<ImagingReport[]>([]);

  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get("tab") || "overview";
  const setActiveTab = (tab: string) => { setSearchParams({ tab }) };

  const fetchAllData = async () => {
    if (!id) {
      console.error("Patient ID is missing from URL");
      setLoading(false);
      return;
    }
    
    setLoading(true);
    
    try {
      const [
        patientRes,
        clinicalNotes,
        histologyReports,
        imagingReports,
      ] = await Promise.all([
        getPatientById(id),
        getClinicalNotesByPatientId(id).catch(err => {
          console.warn("Failed to load clinical notes:", err);
          return [];
        }),
        getHistologyReportsByPatientId(id).catch(err => {
          console.warn("Failed to load histology reports:", err);
          return [];
        }),
        getImagingReports(id).catch(err => {
          console.warn("Failed to load imaging reports:", err);
          return [];
        }),
      ]);
      
      if (!patientRes) {
        throw new Error("Patient data is null or undefined");
      }
      
      setPatient(patientRes);
      setNotes(clinicalNotes);
      setHistologyReportsList(histologyReports);
      setImaginingReport(imagingReports);
    } catch (error: any) {
      console.error("Failed to load patient data:", error);
      const errorMessage = error?.response?.data?.message || error?.message || "Failed to load patient data";
      toast.error(errorMessage);
      setPatient(null);
    } finally {
      setLoading(false);
    }
  };

  //Fetch data
  useEffect(() => {  
    fetchAllData();
  }, [id]);


  // MDT Generation modal state
  const [isGenerateMDTOpen, setIsGenerateMDTOpen] = useState(false);
  const [selectedNotes, setSelectedNotes] = useState<any[]>([]);
  const [selectedReports, setSelectedReports] = useState<any[]>([]);
  const [selectedHistologyReports, setSelectedHistologyReports] = useState<any[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);

  // Edit Patient modal state
  const [isEditPatientOpen, setIsEditPatientOpen] = useState(false);
  const [editForename, setEditForename] = useState('');
  const [editSurname, setEditSurname] = useState('');
  const [editNhsNumber, setEditNhsNumber] = useState('');
  const [editHospitalNumber, setEditHospitalNumber] = useState('');
  const [editMrnNumber, setEditMrnNumber] = useState('');
  const [editGender, setEditGender] = useState('');
  const [editDob, setEditDob] = useState('');

  // Diagnosis fields
  const [isEditDiagnosisOpen, setIsEditDiagnosisOpen] = useState(false);
  const [editCondition, setEditCondition] = useState('');
  const [editIcd10, setEditIcd10] = useState('');
  const [editSubtype, setEditSubtype] = useState('');
  const [editStage, setEditStage] = useState('');
  const [editDateOfDiagnosis, setEditDateOfDiagnosis] = useState('');
  const [editPrognosticScore, setEditPrognosticScore] = useState('');
  const [editTreatmentIntent, setEditTreatmentIntent] = useState('');

  // Function to open edit modal with current patient data
  const openEditPatientModal = () => {
    if (patient) {
      setEditForename(patient.forename || '');
      setEditSurname(patient.surname || '');
      setEditNhsNumber(patient.nhs_number || '');
      setEditHospitalNumber(patient.hospital_number || '');
      setEditMrnNumber(patient.mrn_number || '');
      setEditGender(patient.gender || '');
      setEditDob(patient.date_of_birth || '');
    }
    setIsEditPatientOpen(true);
  };

  // Function to open edit modal with Diagonosis data
  const openEditDiagnosisModal = () => {
    if (patient) {
      setEditCondition(patient.diagnosis?.condition || '');
      setEditIcd10(patient.diagnosis?.icd_10 || '');
      setEditSubtype(patient.diagnosis?.subtype || '');
      setEditStage(patient.diagnosis?.stage || '');
      setEditDateOfDiagnosis(patient.diagnosis?.date_of_diagnosis || '');
      setEditPrognosticScore(patient.diagnosis?.prognostic_score || '');
      setEditTreatmentIntent(patient.diagnosis?.treatment_intent || '');
    }
    setIsEditDiagnosisOpen(true);
  };

  // Function to handle patient update
  const handleUpdatePatient = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const updatedData = {
        forename: editForename,
        surname: editSurname,
        nhs_number: editNhsNumber,
        hospital_number: editHospitalNumber,
        mrn_number: editMrnNumber,
        gender: editGender,
        date_of_birth: editDob,
      };

      if (!updatedData.forename || !updatedData.surname || !updatedData.nhs_number || !updatedData.date_of_birth || !updatedData.gender || !updatedData.hospital_number || !updatedData.mrn_number) {
        toast.error("All fields are required");
      }

      await api.put(`/patient/${id}`, updatedData);

      setPatient({
        ...patient!,
        forename: editForename,
        surname: editSurname,
        nhs_number: editNhsNumber,
        hospital_number: editHospitalNumber,
        mrn_number: editMrnNumber,
        gender: editGender,
        date_of_birth: editDob,
      });

      setIsEditPatientOpen(false);
    } catch (error) {
      console.error("Error updating patient:", error);
    }
  };

  // Function to handle Diagnosis update
  const handleUpdateDiagnosis = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const updatedData = {
        diagnosis: {
          condition: editCondition,
          icd_10: editIcd10,
          subtype: editSubtype,
          stage: editStage,
          date_of_diagnosis: editDateOfDiagnosis,
          prognostic_score: editPrognosticScore,
          treatment_intent: editTreatmentIntent,
        }
      };

      if (!updatedData.diagnosis.condition || !updatedData.diagnosis.icd_10 || !updatedData.diagnosis.subtype || !updatedData.diagnosis.stage || !updatedData.diagnosis.date_of_diagnosis || !updatedData.diagnosis.prognostic_score || !updatedData.diagnosis.treatment_intent) {
        toast.error("All fields are required");
      }

      await api.put(`/patient/${id}`, updatedData);

      setPatient({
        ...patient!,
        diagnosis: updatedData.diagnosis,
      });

      setIsEditDiagnosisOpen(false);
    } catch (error) {
      console.error("Error updating patient:", error);
    }
  };


  const tabs = [
    { id: 'overview', name: 'Overview', icon: User },
    { id: 'clinical-notes', name: 'Clinical Notes', icon: FileText },
    { id: 'imaging', name: 'Imaging Reports', icon: Image },
    { id: 'histology', name: 'Histology Reports', icon: FileText },
    { id: 'mdt-reports', name: 'MDT Reports', icon: FileText },
  ];

  const formatFileSize = (bytes?: number): string => {
    if (!bytes || bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'monitoring':
        return 'bg-primary-100 text-primary-800';
      case 'completed':
        return 'bg-gray-100 text-gray-800';
      case 'approved':
        return 'bg-green-100 text-green-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'signed':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Show loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  // Show error if patient not found
  if (!patient) {
    return (
      <div className="text-center py-12">
        <h2 className="text-xl font-semibold text-gray-900">Patient not found</h2>
        <button onClick={() => navigate('/patients')} className="btn-primary mt-4">
          Back to Patients
        </button>
      </div>
    );
  }

  const patientName = `${patient.forename || ''} ${patient.surname || ''}`.trim() || 'Unknown Patient';
  const patientInitials = `${patient.forename?.[0] || ''}${patient.surname?.[0] || ''}`;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button 
            onClick={() => navigate('/patients')}
            className="p-2 hover:bg-gray-100 rounded-lg"
          >
            <ArrowLeft className="h-5 w-5 text-gray-600" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{patientName}</h1>
            <p className="text-gray-600">NHS ID: {patient.nhs_number}</p>
          </div>
        </div>
        <div className="flex space-x-2">
          <button 
            onClick={() => setIsGenerateMDTOpen(true)}
            className="btn-primary flex items-center space-x-2"
          >
            <FileText className="h-4 w-4" />
            <span>Generate MDT Report</span>
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Side Panel - Patient Details */}
        <div className="lg:col-span-1">
          <div className="card relative">
            <button 
              onClick={openEditPatientModal}
              className="absolute top-3 right-3 btn-secondary flex items-center space-x-2"
            >
              <Edit className="h-4 w-4" />
              <span>Edit</span>
            </button>

            <div className="text-center mb-6">
              <div className="w-20 h-20 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-primary-600">
                  {patientInitials}
                </span>
              </div>
              <h3 className="text-lg font-semibold text-gray-900">{patientName}</h3>
              <p className="text-gray-600">{patient.gender}</p>
            </div>

            <div className="space-y-4">
              <div>
                <h4 className="font-medium text-gray-900 mb-2">Basic Information</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center space-x-2">
                    <User className="h-4 w-4 text-gray-400" />
                    <span className="text-gray-600">NHS number: {patient.nhs_number || '-'}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Clock className="h-4 w-4 text-gray-400" />
                    <span className="text-gray-600">DOB: {patient.date_of_birth || '-'}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className="text-gray-600">Hospital number: {patient.hospital_number || '-'}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className="text-gray-600">MRN number: {patient.mrn_number || '-'}</span>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>


        {/* Main Content */}
        <div className="lg:col-span-3">
          {/* Tabs */}
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center space-x-2 py-2 px-1 border-b-2 font-medium text-sm ${
                    activeTab === tab.id
                      ? 'tab-active'
                      : 'tab-inactive'
                  }`}
                >
                  <tab.icon className="h-4 w-4" />
                  <span>{tab.name}</span>
                </button>
              ))}
            </nav>
          </div>

          {/* Tab Content */}
          <div className="mt-6">
            {activeTab === "overview" && (
              <ClinicalSummary
                patient={patient}
                openEditDiagnosisModal={openEditDiagnosisModal}
              />
            )}

            {activeTab === "clinical-notes" && (
              <ClinicalNotes
              patientId={String(patient.patient_id)}
              onDataChanged={fetchAllData}
              />
            )}

            {activeTab === "imaging" && (
              <Imaging
              formatFileSize={formatFileSize}
              patientId={String(patient.patient_id)}
              onDataChanged={fetchAllData}
              />
            )}
            
            {activeTab === "histology" && (
              <HistologyReports
              formatFileSize={formatFileSize}
              patientId={String(patient.patient_id)}
              onDataChanged={fetchAllData}
            />
            
            )}

            {activeTab === 'mdt-reports' && (
              <MDTReportsTab getStatusColor={getStatusColor} />
            )}

          </div>
        </div>
      </div>


      {/* MDT Generation Modal */}
      <Modal title="Generate MDT Report" isOpen={isGenerateMDTOpen} onClose={() => setIsGenerateMDTOpen(false)} widthClassName="max-w-4xl">
        {isProcessing ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary-600 mx-auto mb-4"></div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Generating MDT Report</h3>
            <p className="text-gray-600">Please wait while we process the selected clinical data and generate your report...</p>
          </div>
        ) : (
          <div className="space-y-6">
            <div className="bg-primary-50 border border-primary-200 rounded-lg p-4">
              <h4 className="font-medium text-primary-900 mb-2">Patient: {patientName}</h4>
              <p className="text-sm text-primary-700">Select clinical notes and imaging reports to include in the MDT report generation.</p>
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Clinical notes selection */}
              <div>
                <h4 className="font-semibold text-gray-900 mb-3">Clinical Notes</h4>
                <div className="space-y-2 max-h-64 overflow-y-auto border border-gray-200 rounded-lg p-3">
                  {notes.map((note) => (
                    <label key={note.id} className="flex items-start space-x-3 p-2 hover:bg-gray-50 rounded">
                      <input
                        type="checkbox"
                        checked={selectedNotes.includes(note.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedNotes([...selectedNotes, note.id]);
                          } else {
                            setSelectedNotes(selectedNotes.filter(id => id !== note.id));
                          }
                        }}
                        className="mt-1 h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                      /> 
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900">{note.title}</p>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              {/* Imaging Reports Selection */}
              <div>
                <h4 className="font-semibold text-gray-900 mb-3">Imaging Reports</h4>
                <div className="space-y-2 max-h-64 overflow-y-auto border border-gray-200 rounded-lg p-3">
                  {imaging.map((report) => (
                    <label key={report.id} className="flex items-start space-x-3 p-2 hover:bg-gray-50 rounded">
                      <input
                        type="checkbox"
                        checked={selectedReports.includes(report.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedReports([...selectedReports, report.id]);
                          } else {
                            setSelectedReports(selectedReports.filter(id => id !== report.id));
                          }
                        }}
                        className="mt-1 h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900">{report.title}</p>
                        <p className="text-xs text-gray-500">{report.type}</p>
                        {report.description && (
                          <p className="text-xs text-gray-600 mt-1 line-clamp-2">{report.description}</p>
                        )}
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              {/* Histology Reports Selection */}
              <div>
                <h4 className="font-semibold text-gray-900 mb-3">Histology Reports</h4>
                <div className="space-y-2 max-h-64 overflow-y-auto border border-gray-200 rounded-lg p-3">
                  {histologyReportsList.map((report) => (
                    <label key={report.id} className="flex items-start space-x-3 p-2 hover:bg-gray-50 rounded">
                      <input
                        type="checkbox"
                        checked={selectedHistologyReports.includes(report.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedHistologyReports([...selectedHistologyReports, report.id]);
                          } else {
                            setSelectedHistologyReports(selectedHistologyReports.filter(id => id !== report.id));
                          }
                        }}
                        className="mt-1 h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900">{report.title}</p>
                        <p className="text-xs text-gray-500">{report.specimen}</p>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            </div> 


            <div className="flex justify-between items-center pt-4 border-t border-gray-200">
              <div className="text-sm text-gray-600">
                Selected: {selectedNotes.length} clinical notes, {selectedReports.length} imaging reports, {selectedHistologyReports.length} histology reports
              </div>
              <div className="flex space-x-3">
                <button
                  onClick={() => setIsGenerateMDTOpen(false)}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                {/* <button
                  onClick={() => {
                    if (selectedNotes.length === 0 || selectedReports.length === 0 || selectedHistologyReports.length === 0) {
                      alert('Please select at least one clinical note, imaging report, and histology report.');
                      return;
                    }
                    setIsProcessing(true);
                    // Simulate processing time
                    setTimeout(() => {
                      setIsProcessing(false);
                      setIsGenerateMDTOpen(false);
                      setSelectedNotes([]);
                      setSelectedReports([]);
                      setSelectedHistologyReports([]);
                      // Generate a new MDT report ID and navigate to it
                      const newReportId = Date.now().toString();
                      navigate(`/reports/${newReportId}`);
                    }, 3000);
                  }}
                  className="btn-primary"
                >
                  Generate MDT Report
                </button> */}
                <button
                  onClick={async () => {
                    if (
                      selectedNotes.length === 0 || selectedReports.length === 0 || selectedHistologyReports.length === 0
                    ) {
                      toast.error("Please select at least one clinical note, imaging report, and histology report.");
                      return;
                    }

                    try {
                      setIsProcessing(true);

                      const result = await createMDTReport({
                        patient_id: String(patient.patient_id),
                        created_by: user?.id,
                        clinical_note_ids: selectedNotes,
                        imaging_report_ids: selectedReports,
                        histology_report_ids: selectedHistologyReports,
                      });

                      toast.success("MDT report generated successfully");

                      setIsGenerateMDTOpen(false);
                      setSelectedNotes([]);
                      setSelectedReports([]);
                      setSelectedHistologyReports([]);

                      // Navigate to real MDT report page
                      navigate(`/reports/${result.id}`);
                    } catch (error: any) {
                      console.error(error);
                      toast.error(
                        error?.response?.data?.message ||
                          "Failed to generate MDT report"
                      );
                    } finally {
                      setIsProcessing(false);
                    }
                  }}
                  disabled={isProcessing}
                  className="btn-primary"
                >
                  {isProcessing ? "Generating MDT Report..." : "Generate MDT Report"}
                </button>

              </div>
            </div>
          </div>
        )}
      </Modal>

      {/* Edit Patient Modal */}
      <Modal title="Edit Patient" isOpen={isEditPatientOpen} onClose={() => setIsEditPatientOpen(false)} widthClassName="max-w-4xl">
        <form onSubmit={handleUpdatePatient} className="space-y-6">
          <div>
            <h4 className="font-semibold text-gray-900 mb-3">Basic Information</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Forename</label>
                <input
                  value={editForename}
                  onChange={(e) => setEditForename(e.target.value)}
                  className="input-field mt-1"
                  placeholder="Enter forename"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Surname</label>
                <input
                  value={editSurname}
                  onChange={(e) => setEditSurname(e.target.value)}
                  className="input-field mt-1"
                  placeholder="Enter surname"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">NHS Number</label>
                <input
                  value={editNhsNumber}
                  onChange={(e) => setEditNhsNumber(e.target.value)}
                  className="input-field mt-1"
                  placeholder="Enter NHS number"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Date of Birth</label>
                <input
                  type="date"
                  value={editDob}
                  onChange={(e) => setEditDob(e.target.value)}
                  className="input-field mt-1"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Gender</label>
                <CustomSelect
                  value={editGender}
                  onChange={(val) => setEditGender(val)}
                  options={[
                    { value: '', label: 'Select gender' },
                    { value: 'Male', label: 'Male' },
                    { value: 'Female', label: 'Female' },
                    { value: 'Other', label: 'Other' },
                  ]}
                  placeholder="Select gender"
                  className="mt-1"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Hospital Number</label>
                <input
                  value={editHospitalNumber}
                  onChange={(e) => setEditHospitalNumber(e.target.value)}
                  className="input-field mt-1"
                  placeholder="Enter hospital number"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">MRN Number</label>
                <input
                  value={editMrnNumber}
                  onChange={(e) => setEditMrnNumber(e.target.value)}
                  className="input-field mt-1"
                  placeholder="Enter MRN number"
                />
              </div>
            </div>
          </div>

          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={() => setIsEditPatientOpen(false)}
              className="btn-secondary"
            >
              Cancel
            </button>
            <button type="submit" className="btn-primary">
              Save Changes
            </button>
          </div>
        </form>
      </Modal>
      
      {/* edit Diagonosis modal */}
      <Modal title="Edit Diagonosis" isOpen={isEditDiagnosisOpen} onClose={() => setIsEditDiagnosisOpen(false)} widthClassName="max-w-4xl">
        <form onSubmit={handleUpdateDiagnosis} className="space-y-6">
          <div>
            <h4 className="font-semibold text-gray-900 mb-3">Diagnosis Information</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Condition</label>
                <input
                  value={editCondition}
                  onChange={(e) => setEditCondition(e.target.value)}
                  className="input-field mt-1"
                  placeholder="e.g., Diffuse Large B-Cell Lymphoma"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">ICD-10 Code</label>
                <input
                  value={editIcd10}
                  onChange={(e) => setEditIcd10(e.target.value)}
                  className="input-field mt-1"
                  placeholder="e.g., C83.3"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Subtype</label>
                <input
                  value={editSubtype}
                  onChange={(e) => setEditSubtype(e.target.value)}
                  className="input-field mt-1"
                  placeholder="e.g., Germinal Center B-Cell"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Stage</label>
                <CustomSelect
                  value={editStage}
                  onChange={(val) => setEditStage(val)}
                  options={[
                    { value: '', label: 'Select stage' },
                    { value: 'Stage I', label: 'Stage I' },
                    { value: 'Stage II', label: 'Stage II' },
                    { value: 'Stage III', label: 'Stage III' },
                    { value: 'Stage IV', label: 'Stage IV' },
                  ]}
                  placeholder="Select stage"
                  className="mt-1"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Date of Diagnosis</label>
                <input
                  type="date"
                  value={editDateOfDiagnosis}
                  onChange={(e) => setEditDateOfDiagnosis(e.target.value)}
                  className="input-field mt-1"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Prognostic Score</label>
                <input
                  value={editPrognosticScore}
                  onChange={(e) => setEditPrognosticScore(e.target.value)}
                  className="input-field mt-1"
                  placeholder="e.g., IPI Score: 2"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700">Treatment Intent</label>
                <CustomSelect
                  value={editTreatmentIntent}
                  onChange={(val) => setEditTreatmentIntent(val)}
                  options={[
                    { value: '', label: 'Select treatment intent' },
                    { value: 'Curative', label: 'Curative' },
                    { value: 'Palliative', label: 'Palliative' },
                    { value: 'Supportive', label: 'Supportive' },
                  ]}
                  placeholder="Select treatment intent"
                  className="mt-1"
                />
              </div>
            </div>
          </div> 

          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={() => setIsEditDiagnosisOpen(false)}
              className="btn-secondary"
            >
              Cancel
            </button>
            <button type="submit" className="btn-primary">
              Save Changes
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default PatientProfile;
